import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  Icompany,
  IdataArrayResponse,
  IdeleteOneResponse,
  IecommerceOrder,
  IfilterProps,
  IunreliableVendorHistory
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

type TunreliableVendorHistoryProps = IunreliableVendorHistory<Icompany, IecommerceOrder>;

/**
 * Represents a history record for tracking unreliable vendor activities.
 * This class provides methods to manage and query vendor reliability history entries,
 * including marking vendors as unreliable and recording related order information.
 *
 * @template TunreliableVendorHistoryProps - The type definition for unreliable vendor history properties
 * @extends DatabaseAuto - Extends the base DatabaseAuto class for common database operations
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UnreliableVendorHistory} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const unreliableVendorHistory = await UnreliableVendorHistory.getAll();
 *
 * const unreliableVendorHistory = await UnreliableVendorHistory.filterAll({
 *
 * });
 *
 * const unreliableVendorHistory = await UnreliableVendorHistory.bulkDelete({
 *
 * });
 *
 * const unreliableVendorHistory = await UnreliableVendorHistory.getOne('resourceId');
 *
 * const unreliableVendorHistory = await UnreliableVendorHistory.remove();
 *
 * ```
 */
export class UnreliableVendorHistory extends DatabaseAuto {
  /** Base API endpoint for unreliable vendor history operations */
  private static readonly baseUrl = '/un-reliable-vendor-history';

  /** The company associated with this history record */
  readonly company: TunreliableVendorHistoryProps['company'];

  /** The reason for marking the vendor as unreliable */
  readonly reason: TunreliableVendorHistoryProps['reason'];

  /** The action taken regarding the vendor's reliability status */
  readonly action: TunreliableVendorHistoryProps['action'];

  /** The e-commerce order related to this reliability event */
  readonly ecommerceOrder: TunreliableVendorHistoryProps['ecommerceOrder'];

  /**
   * Creates a new instance of UnreliableVendorHistory.
   * @param {TunreliableVendorHistoryProps} data - The data used to initialize the history record
   * @throws {Error} Throws an error if no data is provided
   */
  constructor(data: TunreliableVendorHistoryProps) {
    super(data);

    this.company = data.company;
    this.reason = data.reason;
    this.action = data.action;
    this.ecommerceOrder = data.ecommerceOrder;
  }

  /**
   * Retrieves all unreliable vendor history records with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: UnreliableVendorHistory[] }>}
   * An object containing the total count and an array of history records
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TunreliableVendorHistoryProps>>(`${UnreliableVendorHistory.baseUrl}?${query}`);
    const unreliableVendorHistorys = await lastValueFrom(observer$);

    return {
      count: unreliableVendorHistorys.count,
      dataArr: unreliableVendorHistorys.data.map((val) => new UnreliableVendorHistory(val))
    };
  }

  /**
   * Filters unreliable vendor history records based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: UnreliableVendorHistory[] }>}
   * An object containing the total count and an array of filtered history records
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TunreliableVendorHistoryProps>>(
        `${UnreliableVendorHistory.baseUrl}/filter`,
        filter
      );
    const unreliableVendorHistorys = await lastValueFrom(observer$);

    return {
      count: unreliableVendorHistorys.count,
      dataArr: unreliableVendorHistorys.data.map((val) => new UnreliableVendorHistory(val))
    };
  }

  /**
   * Retrieves a single unreliable vendor history record by its ID.
   * @param {string} resourceId - The unique identifier of the history record to retrieve
   * @returns {Promise<UnreliableVendorHistory>} The requested history record
   * @throws {Error} If the record is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TunreliableVendorHistoryProps>(`${UnreliableVendorHistory.baseUrl}/${resourceId}`);
    const unreliableVendorHistory = await lastValueFrom(observer$);

    return new UnreliableVendorHistory(unreliableVendorHistory);
  }

  /**
   * Deletes multiple unreliable vendor history records based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which records to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${UnreliableVendorHistory.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current unreliable vendor history record from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the record doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${UnreliableVendorHistory.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
